import React, { useState } from 'react';
import { Modal, Button, Message, ToastContext } from '@weezevent/nacre';

import useAppContext from '../../../context/useAppContext';
import DragdropRates from './dragDropRates';
import LoadingCard from '../../../components/loadingCard';
import { compute_sum_float } from '../utils';
import { useTranslation } from 'react-i18next';
import RatesTreeModel from '../../../models/ratesTreeModel';
import Style from './modalSortRates.module.css';

//flat tree and rearranges so that the groups are always after the rates of the same level
const browseGroupChildren = item => {
    let _flatTree = [];
    _flatTree.push(item);

    if (item.group) {
        item.children
            .filter(i => !i.group)
            .forEach(rate => {
                _flatTree.push(rate);
            });
        item.children
            .filter(i => i.group)
            .forEach(group => {
                _flatTree = [..._flatTree, ...browseGroupChildren(group)];
            });
    }

    return _flatTree;
};

const flatTree = tree => {
    let _flatTree = [];
    tree.filter(i => !i.group).forEach(rate => {
        _flatTree.push(rate);
    });
    tree.filter(i => i.group).forEach(group => {
        _flatTree = [..._flatTree, ...browseGroupChildren(group)];
    });

    return _flatTree;
};

// Contruct tree for the component DragDropRates
const constructTree = (parentId = null, groups, rates) => {
    let tree = [];
    rates
        .filter(rate => rate.group === parentId)
        .sort((r1, r2) => r1.display_order - r2.display_order)
        .forEach(rate => {
            const price = rate.rate_template?.price || rate.price;
            const computedPrice = rate.fees?.length && rate.fees.reduce((sum, { amount }) => compute_sum_float(sum, parseFloat(amount) || 0), parseFloat(price || 0));
            tree.push({ title: rate.rate_template?.name || rate.name, price: computedPrice || price, key: rate.pk, group: false, parent: parentId });
        });
    groups
        .filter(group => group.parent === parentId)
        .sort((g1, g2) => g1.display_order - g2.display_order)
        .forEach(group => {
            let children = constructTree(group.pk, groups, rates);
            tree.push({ title: group.group_template?.name || group.name, key: group.pk, children: children, group: true, parent: parentId });
        });

    return tree;
};

export default function ModalSortRates({ open, rates, groups, onClose, reloadList }) {
    const { t } = useTranslation();

    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [displayTips, setDisplayTips] = useState(true);
    const [{ organization, event }] = useAppContext();
    const toast = React.useContext(ToastContext);

    React.useEffect(() => {
        if (loading) {
            setTree(constructTree(null, groups, rates));
            setLoading(false);
        }
    }, [groups, rates, loading]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        let _rates = [];
        let _groups = [];
        let _flatTree = flatTree(tree);
        _flatTree.forEach((item, index) => {
            if (item.group) {
                let group = groups.find(g => g.pk === item.key);
                //group.parent can be compared here, because in the initial request of the list, there is not the expand of the groups
                if (group) {
                    _groups.push({ id: group.id, display_order: index, parent: item.parent });
                }
                return;
            }

            let rate = rates.find(r => r.pk === item.key);
            //rate.group can be compared here, because  in the initial request of the list, there is not the expand of the groups
            if (rate) {
                _rates.push({ id: rate.id, display_order: index, group: item.parent });
            }
        });

        RatesTreeModel.rateTreeUpdate(
            { rates: _rates, groups: _groups },
            {
                organizationId: organization.id,
                eventId: event.pk
            }
        )
            .then(() => {
                onClose();
                reloadList(false);
                toast.success(t('ticket.rates.sortRates.toastSuccess'));
            })
            .catch(() => {
                toast.error(t('ticket.rates.sortRates.toastError'));
                setIsSubmitting(false);
            });
    }, [rates, groups, tree, reloadList, onClose]);

    return (
        <Modal
            className={'modal-sort'}
            allowExternalClosing={false}
            size="extralarge"
            open={open}
            onClose={onClose}
            title={t('ticket.rates.sortRates.title')}
            subtitle={t('ticket.rates.sortRates.subtitle')}
            submitButton={<Button primary onClick={handleSubmit} label={t('ticket.common.action.save')} isLoading={isSubmitting} disabled={isSubmitting} />}
            cancelButton={<Button onClick={onClose} label={t('ticket.common.action.cancel')} disabled={isSubmitting} />}
        >
            <LoadingCard loading={loading || isSubmitting} label={isSubmitting ? t('ticket.common.inProgress') : null}>
                <Message className={Style['tips']} type="tips" title={t('notifications.tips_title')} mini onClose={() => setDisplayTips(false)} visible={displayTips}>
                    {t('ticket.rates.sortRates.tips')}
                </Message>
                <DragdropRates tree={tree} onChangeTree={setTree} />
            </LoadingCard>
        </Modal>
    );
}
